<script setup lang="ts">
type SortOrder = "asc" | "desc" | "name-asc" | "name-desc";
type Orientation = "vertical" | "horizontal";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);

const maxItems = field<number | undefined>("maxItems", undefined);
const sortOrder = field<SortOrder>("sortOrder", "desc");
const minScore = field<number | undefined>("minScore", undefined);
const orientation = field<Orientation>("orientation", "vertical");
const showDescriptions = field<boolean>("showDescriptions", false);

const excludePatterns = computed({
  get: () => ((block.value?.excludePatterns ?? []) as string[]).join(", "),
  set: (value: string) => {
    patch({
      excludePatterns: value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    });
  },
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:chart-column-big" accent="reporting">
    <EditorField :label="t('maxItemsLabel')">
      <Input v-model.number="maxItems" type="number" min="0" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('sortOrderLabel')">
      <Select :model-value="sortOrder" @update:model-value="(value) => (sortOrder = value as SortOrder)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="desc">{{ t("descending") }}</SelectItem>
          <SelectItem value="asc">{{ t("ascending") }}</SelectItem>
          <SelectItem value="name-asc">{{ t("nameAscending") }}</SelectItem>
          <SelectItem value="name-desc">{{ t("nameDescending") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('excludePatternsLabel')" :hint="t('excludePatternsHint')">
      <Input v-model="excludePatterns" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="OVERALL" />
    </EditorField>

    <EditorField :label="t('minScoreLabel')">
      <Input v-model.number="minScore" type="number" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('orientationLabel')">
      <Select :model-value="orientation" @update:model-value="(value) => (orientation = value as Orientation)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="vertical">{{ t("vertical") }}</SelectItem>
          <SelectItem value="horizontal">{{ t("horizontal") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('showDescriptionsLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("showDescriptionsHint") }}</span>
        <Switch v-model="showDescriptions" />
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Development Areas"
  maxItemsLabel: "Max Items"
  sortOrderLabel: "Sort Order"
  descending: "Descending"
  ascending: "Ascending"
  nameAscending: "Name Ascending"
  nameDescending: "Name Descending"
  excludePatternsLabel: "Exclude Patterns"
  excludePatternsHint: "Comma-separated construct names or patterns to exclude."
  minScoreLabel: "Minimum Score"
  orientationLabel: "Orientation"
  vertical: "Vertical"
  horizontal: "Horizontal"
  showDescriptionsLabel: "Descriptions"
  showDescriptionsHint: "Show construct descriptions when supported."
</i18n>
